/**
 * **Fleet** is a place: the header word opens it, and its pages show as a row
 * of tabs under the header, only while one of them is open. Today it holds
 * Live status. Sketch: docs/design/sketches/fleet-menu.html, option B.
 *
 * `fleet-nav` is the header word, `fleet-tabs` the row. Both pick a page with a
 * `section-pick` event; `app-root` switches to it.
 */

import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { serverFlags, serverManaged } from '../../state/feature-flags.ts';
import { isPageVisible, pageLock, pageVisibility, type PageId } from '../../state/page-visibility.ts';
import { myAccess, type Section } from '../../state/store.ts';
import { lockedNav } from '../../ui/locked-nav.ts';

/** The pages inside Fleet, in tab order. */
const FLEET_PAGES: { section: Section & PageId; label: string; title: string }[] = [
  { section: 'live-status', label: 'Live status', title: 'Whether live data arrives from every ship' },
];

export function isFleetSection(section: string): boolean {
  return FLEET_PAGES.some((p) => p.section === section);
}

const STORES = [pageVisibility, myAccess, serverFlags, serverManaged];

function pick(el: HTMLElement, section: Section): void {
  el.dispatchEvent(new CustomEvent('section-pick', { detail: section, bubbles: true }));
}

/** The header word. Opens the first Fleet page this person may see. */
@customElement('fleet-nav')
export class FleetNav extends LightElement {
  @property() section = '';
  private store = new StoreController(this, STORES);

  override render() {
    const first = FLEET_PAGES.find((p) => isPageVisible(p.section));
    if (!first) {
      const lock = FLEET_PAGES.map((p) => pageLock(p.section)).find(Boolean);
      return lock ? lockedNav('Fleet', lock, 'primary-nav-btn') : nothing;
    }
    const active = isFleetSection(this.section);
    return html`<button
      class="primary-nav-btn ${active ? 'active' : ''}"
      aria-current=${active ? 'page' : nothing}
      title="Fleet-wide pages"
      @click=${() => pick(this, first.section)}
    >
      Fleet
    </button>`;
  }
}

/** The row of Fleet pages under the header. Empty outside Fleet. */
@customElement('fleet-tabs')
export class FleetTabs extends LightElement {
  @property() section = '';
  private store = new StoreController(this, STORES);

  override render() {
    if (!isFleetSection(this.section)) return nothing;
    return html`<nav class="fleet-tabs" aria-label="Fleet pages">
      ${FLEET_PAGES.map((p) => {
        const on = p.section === this.section;
        if (isPageVisible(p.section)) {
          return html`<button
            class="fleet-tab ${on ? 'is-on' : ''}"
            aria-current=${on ? 'page' : nothing}
            title=${p.title}
            @click=${() => pick(this, p.section)}
          >
            ${p.label}
          </button>`;
        }
        const lock = pageLock(p.section);
        return lock ? lockedNav(p.label, lock, 'fleet-tab') : nothing;
      })}
    </nav>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fleet-nav': FleetNav;
    'fleet-tabs': FleetTabs;
  }
}
